export const queryKeys = {
  // `all()` exists as an invalidation root.
  auth: {
    all: () => ["auth"] as const,
    // GET /auth/organization
    organization: () => [...queryKeys.auth.all(), "organization"] as const,
  },

  users: {
    all: () => ["users"] as const,
    // GET /users/me
    me: () => [...queryKeys.users.all(), "me"] as const,
    // GET /users
    lists: () => [...queryKeys.users.all(), "list"] as const,
  },

  financiers: {
    all: () => ["financiers"] as const,
    lists: () => [...queryKeys.financiers.all(), "list"] as const,
    // GET /financiers/:id
    detail: (id: string) =>
      [...queryKeys.financiers.all(), "detail", id] as const,
    // GET /financiers/:id/withdrawals
    withdrawals: (id: string) =>
      [...queryKeys.financiers.all(), "detail", id, "withdrawals"] as const,
    // GET /financiers/:id/investments
    investments: (id: string) =>
      [...queryKeys.financiers.all(), "detail", id, "investments"] as const,
    // GET /financiers/:id/pools
    pools: (id: string) =>
      [...queryKeys.financiers.all(), "detail", id, "pools"] as const,
  },

  merchants: {
    all: () => ["merchants"] as const,
    lists: () => [...queryKeys.merchants.all(), "list"] as const,
    // GET /merchants/:id
    detail: (id: string) =>
      [...queryKeys.merchants.all(), "detail", id] as const,
    // GET /merchants/:id/pools/current
    activePool: (id: string) =>
      [...queryKeys.merchants.all(), "detail", id, "active-pool"] as const,
    // GET /merchants/:id/summary
    summary: (id: string) =>
      [...queryKeys.merchants.all(), "detail", id, "summary"] as const,
  },

  pools: {
    all: () => ["pools"] as const,
    openLists: () => [...queryKeys.pools.all(), "open-list"] as const,
    // Invalidation root for pool-scoped queries (no fetch hook — pool metadata lives
    // under merchants.activePool). Prefix-matches all sub-keys below.
    detail: (id: string) => [...queryKeys.pools.all(), "detail", id] as const,
    // GET /pools/:poolId/receivables/financing-rounds
    receivablesRounds: (poolId: string) =>
      [...queryKeys.pools.detail(poolId), "receivablesRounds"] as const,
    // GET /pools/:poolId/inventory/financing-rounds
    inventoryRounds: (poolId: string) =>
      [...queryKeys.pools.detail(poolId), "inventoryRounds"] as const,
    // GET /pools/:poolId/inventory/assets
    inventoryAssets: (poolId: string) =>
      [...queryKeys.pools.detail(poolId), "inventoryAssets"] as const,
    // GET /pools/:poolId/inventory/assets/:assetId
    inventoryAssetDetail: (poolId: string, assetId: string) =>
      [...queryKeys.pools.detail(poolId), "inventoryAssets", assetId] as const,
    // GET /pools/:poolId/receivables/assets
    receivablesAssets: (poolId: string) =>
      [...queryKeys.pools.detail(poolId), "receivablesAssets"] as const,
    // GET /pools/:poolId/receivables/assets/:assetId
    receivablesAssetDetail: (poolId: string, assetId: string) =>
      [...queryKeys.pools.detail(poolId), "receivablesAssets", assetId] as const,
    // GET /pools/:id/coded-terms
    codedTerms: (id: string) =>
      [...queryKeys.pools.detail(id), "coded-terms"] as const,
    // GET /pools/:id/deposits/:requestId
    depositStatus: (poolId: string, requestId: string) =>
      [...queryKeys.pools.detail(poolId), "deposits", requestId] as const,
    // GET /pools/:poolId/investments/:investmentId/withdrawals/:requestId
    withdrawalStatus: (
      poolId: string,
      investmentId: string,
      requestId: string,
    ) =>
      [
        ...queryKeys.pools.detail(poolId),
        "investments",
        investmentId,
        "withdrawals",
        requestId,
      ] as const,
    // GET /pools/:id/interest-payments/:requestId
    interestPaymentStatus: (poolId: string, requestId: string) =>
      [
        ...queryKeys.pools.detail(poolId),
        "interest-payments",
        requestId,
      ] as const,
    // GET /pools/:id/financing-repayments/:requestId
    financingRepaymentStatus: (poolId: string, requestId: string) =>
      [
        ...queryKeys.pools.detail(poolId),
        "financing-repayments",
        requestId,
      ] as const,
  },

  customers: {
    all: () => ["customers"] as const,
    lists: () => [...queryKeys.customers.all(), "list"] as const,
  },

  tokenOperations: {
    all: () => ["tokenOperations"] as const,
    lists: () => [...queryKeys.tokenOperations.all(), "list"] as const,
    // GET /token-operations/my-reference-number
    myReferenceNumber: () =>
      [...queryKeys.tokenOperations.all(), "my-reference-number"] as const,
  },

  blockchain: {
    all: () => ["blockchain"] as const,
    // Addresses are lowercased so wallet/publicKey casing differences don't split the cache.
    balance: (address: string) =>
      [...queryKeys.blockchain.all(), "balance", address.toLowerCase()] as const,
    // GET /pools/:id/chain-state — keyed on pool UUID, not the on-chain address.
    chainState: (poolId: string) =>
      [...queryKeys.blockchain.all(), "chain-state", poolId] as const,
    totalSupply: () => [...queryKeys.blockchain.all(), "totalSupply"] as const,
  },
};
